import BasicTable from 'components/Form/Table';
import TableWrapper from 'components/TableWrapper';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { toast } from 'react-toastify';
import { Read } from 'services/banking';
import { PerfilContent, StatusContent } from 'ui/styled';
import { marcadorDiferente, normalizeResponse, selectColors } from 'utils';
import { formatElapsedTime, parseCurrency } from 'utils/parsers';
import { formatadores, normalizeName } from 'utils/validators';

export default function BankingCredito() {
  const { user, setModal, filterExpression } = useContext(CoreContext);
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [registers, setRegisters] = useState([]);
  const [tick, setTick] = useState(0);
  const [distribuidorId, setDistribuidorId] = useState(null);

  // Atualiza a cada 2 minutos
  useEffect(() => {
    const interval = setInterval(() => setTick((prev) => prev + 1), 120000);
    return () => clearInterval(interval);
  }, []);

  const init = useCallback(async () => {
    setLoading(true);

    try {
      const result = await Read(
        '?filters[status]=aprovar&filters[status]=alcada&filters[status]=aprovado'
      );
      const data = normalizeResponse(result);
      setDistribuidorId(user.distribuidor.id);
      if (!data.length) {
        setRegisters([]);
        setLoading(false);
        return;
      }
      const sorted = data.sort((a, b) => (b.id || 0) - (a.id || 0));
      const next = sorted.map((item) => ({
        ...item,
        accepted: (item.bancos || []).find((f) => f.aceito),
      }));
      setRegisters(next);
    } catch (err) {
      console.error('Erro ao carregar propostas:', err);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: 'Id',
        sortable: true,
        ref: 'id',
        renderCell: ({ row }) => {
          // Condição: Se o campo 'curadoria_analise' for 'Analisado'
          const isAnalisado = row?.curadoria_analise === 'Analisado';
          const id = row.idproposta;
          if (isAnalisado) {
            return marcadorDiferente(id, '!');
          }
          return id;
        },
      },
      {
        title: 'Data',
        sortable: true,
        ref: 'date',
        renderCell: ({ row }) => formatadores.TIMESTAMP_TO_BR(row.date),
      },
      {
        title: 'SLA',
        renderCell: ({ row }) => {
          const diffDays = formatadores.DIFF_DAYS_NOW(row?.date);
          return (
            <span style={{ color: diffDays >= 2 ? 'red' : 'inherit' }}>
              {formatElapsedTime(row?.date)}
            </span>
          );
        },
      },
      // Inserir coluna "Distribuidora" condicionalmente
      ...(user?.administrador ||
      (user?.distribuidor && user?.distribuidores?.length > 0)
        ? [
            {
              title: 'Distribuidora',
              sortable: true,
              ref: 'distributor',
            },
          ]
        : []),
      { title: 'Integrador', sortable: true, ref: 'integrator' },
      { title: 'Cliente', sortable: true, ref: 'customer' },
      {
        title: 'Perfil',
        sortable: true,
        ref: 'bureau_perfil',
        align: 'center',
        renderCell: ({ row }) => (
          <PerfilContent
            color={selectColors(row?.crivo_payload?.client_profile)}
          >
            {row?.crivo_payload?.client_profile}
          </PerfilContent>
        ),
      },
      { title: 'Taxa', ref: 'taxa' },
      { title: 'Valor', ref: 'value' },
      { title: 'Entrada', ref: 'entrada' },
      { title: 'Prazo', ref: 'prazo' },
      { title: 'PMT', ref: 'valor_parcelas' },
      {
        title: '1º Vencimento',
        ref: 'data_vencimento',
        renderCell: ({ row }) =>
          formatadores.TIMESTAMP_TO_BR(row.data_vencimento),
      },
      {
        title: 'TAG',
        ref: 'status',
        renderCell: ({ row }) => {
          return (
            <StatusContent
              color={selectColors(row?.status)}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                if (row?.status === 'Parecer' && user?.banking) {
                  setModal({
                    type: 'parecer',
                    data: row,
                    origem: 'negocios',
                    reload: () => init(),
                    reloadWindow: () => history.go(0),
                  });
                } else if (
                  row?.status === 'Pendente de Alçada' &&
                  user?.banking
                ) {
                  setModal({
                    type: 'parecer',
                    data: row,
                    origem: 'negocios',
                    reload: () => init(),
                    reloadWindow: () => history.go(0),
                  });
                } else if (row?.status === 'Retorno' && user?.banking) {
                  setModal({
                    type: 'credito_erro',
                    id: row.id,
                    origem: 'creditoerror',
                    reload: () => init(),
                  });
                }
              }}
            >
              {row?.status}
            </StatusContent>
          );
        },
      },
    ];
    return baseColumns;
  }, [user, init, setModal, history]);

  const rows = useMemo(() => {
    return (registers || []).map((a) => ({
      id: a?.id,
      date: a?.data_credito,
      formatedDate: formatadores.TIMESTAMP_TO_BR(a?.data_credito),
      cliente: a?.cliente,
      distributor: normalizeName(a?.distribuidor?.trade_name) || '-',
      distributorId: a?.distribuidor?.id,
      integrator:
        normalizeName(a?.proposta_financeira?.integrador?.nome_fantasia) || '-',
      customer: normalizeName(a?.cliente?.nome_completo) || '-',
      value: parseCurrency(
        a?.proposta_financeira?.informacoes_projeto?.custo_total_projeto
      ),
      status:
        a?.status === 'aprovar'
          ? 'Parecer'
          : a?.status === 'aprovado'
            ? 'Retorno'
            : 'Pendente de Alçada',
      id_proposal: a?.proposta_financeira?.id,
      proposta_financeira: a?.proposta_financeira,
      documento_identificacao: a?.cliente?.documento_identificacao,
      comprovante_renda: a?.cliente?.comprovante_renda,
      crivo_payload: a?.crivo_payload,
      taxa: `${a?.proposta_financeira?.propostas_banco[0]?.taxa}%`,
      taxas:
        a?.proposta_financeira?.propostas_banco[0]?.banco?.taxas?.find(
          (t) => t.padrao
        ) || null,
      taxas_all:
        a?.proposta_financeira?.propostas_banco[0]?.banco?.taxas || null,
      data_vencimento: a?.proposta_financeira?.propostas_banco[0]?.data,
      valor_parcelas: parseCurrency(
        a?.proposta_financeira?.propostas_banco[0]?.valor_parcelas
      ),
      prazo: a?.proposta_financeira?.propostas_banco[0]?.parcelas,
      entrada: parseCurrency(
        a?.proposta_financeira?.propostas_banco[0]?.valor_entrada
      ),
      avalista_doc_ident: a?.avalista_doc_ident,
      avalista_comp_renda: a?.avalista_comp_renda,
      avalista_nome: a?.avalista_nome,
      avalista_cpf: a?.avalista_cpf,
      avalista_bureau_score: a?.avalista_bureau_score,
      bureau_observacoes: a?.bureau_observacoes,
      parecer: a?.parecer.map((parecer) => parecer),
      curadoria_analise: a?.proposta_financeira?.curadoria_analise,
      observationCheckList: a?.observationCheckList,
      idproposta: a?.proposta_financeira.id,
      documento_bureau: a?.documento_bureau,
      retorno_erro: a?.retorno_erro,
      rating_julia: a?.rating_julia,
      taxa_sugerida: a?.taxa_sugerida,
      taxas_calculo: a?.distribuidor.banking_regras_gerais,
    }));
  }, [registers]);
  useEffect(() => init(), [init, tick]);

  return (
    <ContainerAuthenticated>
      <TableWrapper title="Crédito" search distribuidor={distribuidorId}>
        <BasicTable
          loading={loading}
          columns={columns}
          rows={rows?.filter(filterExpression)}
          pagination
        />
      </TableWrapper>
    </ContainerAuthenticated>
  );
}
